import { readonly, ref, type Ref } from 'vue'

import type { ApiSession } from './client'
import type { ApiEnvelope, RefreshResponseDto } from './types'

/**
 * Relácia prihláseného používateľa.
 *
 * Access token je zámerne **len v pamäti** — po refreshi stránky sa získa nový
 * cez refresh token, ktorý je httpOnly cookie. V `localStorage` by ho vedel
 * prečítať ľubovoľný XSS.
 *
 * Modul je mimo Pinie schválne: klient API ho potrebuje skôr, než sa Pinia
 * nainštaluje, a takto medzi nimi nevzniká kruhová závislosť.
 */
const accessToken = ref<string | null>(null)

/** Čítanie pre UI (napr. „ste prihlásený“); zápis ide výhradne cez funkcie nižšie. */
export const currentAccessToken: Readonly<Ref<string | null>> = readonly(accessToken)

type AuthLostHandler = () => void

const authLostHandlers = new Set<AuthLostHandler>()

/** Router si sem zaregistruje presmerovanie na prihlásenie. */
export function onAuthLost(handler: AuthLostHandler): () => void {
	authLostHandlers.add(handler)

	return () => authLostHandlers.delete(handler)
}

export function setAccessToken(token: string | null): void {
	accessToken.value = token
}

/** Základ ciest API; predvolene rovnaký pôvod, aby prehliadač poslal refresh cookie. */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

/**
 * Vymení refresh cookie za nový access token.
 *
 * Zámerne obchádza `ApiClient` — inak by 401 z obnovenia spustilo ďalšie
 * obnovenie a vznikla by slučka.
 */
async function requestRefresh(): Promise<string | null> {
	let response: Response
	try {
		response = await globalThis.fetch(`${API_BASE_URL}/auth/refresh`, {
			method: 'POST',
			headers: { Accept: 'application/json' },
			credentials: 'include',
		})
	} catch {
		return null
	}

	if (!response.ok) {
		return null
	}

	const payload = (await response.json()) as ApiEnvelope<RefreshResponseDto>

	return payload.data.accessToken
}

/** Implementácia `ApiSession` pre bežnú prevádzku. */
export const browserSession: ApiSession = {
	getAccessToken: () => accessToken.value,
	setAccessToken,
	refresh: requestRefresh,
	onAuthLost: () => {
		accessToken.value = null
		for (const handler of authLostHandlers) {
			handler()
		}
	},
}
